import { format } from 'date-fns-tz';
import { Utilities } from '../../types';
import { DateObject, Dayjs } from '../dateObject';
import { filterNotEmpty } from '../listHelpers';
import {
  type DateFormatModel,
  type DateTimeFormatConfigModel,
  DEFAULT_DATE_TIME_FORMAT,
  SEPARATOR_DICTIONARY,
  ThreeLetterDateFormats,
  type TreatAsUTC
} from './dateTimeFormatConfigModel';
import { useDateTimeFormatContext } from './DateTimeFormatProvider';
import { buildDateFormatString } from './v2/buildDateFormatString';
import {
  DAY_OF_MONTH_FORMAT,
  DAY_OF_WEEK_FORMAT,
  MONTH_FORMAT,
  TIME_FORMAT,
  TIMEZONE_FORMAT,
  YEAR_FORMAT
} from './v2/DATE_FORMATS';
import { DatePart, Formats, type IanaTimeZoneName, type Separator } from './v2/formatTypes';
import { parseDate } from './v2/parseDate';

export type DateTimeFormatterV2Options = {
  /**
   * @default false
   */
  utc?: TreatAsUTC;
  /**
   * @default undefined/false
   */
  time?: boolean | 'with_seconds';
  /**
   * @default false
   */
  timeZone?: boolean | IanaTimeZoneName;
  /**
   * @default true
   */
  numberMonth?: boolean;
  /**
   * @default '-'
   */
  noDatePlaceholder?: string;
  /**
   * @default ['YEAR', 'MONTH', 'DAY_OF_MONTH'] (plus 'TIME' and 'TIMEZONE' if time and timeZone are true)
   */
  display?: DatePart[];
  format?: Partial<Formats>;
  config?: {
    separator?: Separator;
    dateOrder?: DateFormatModel;
  };
};

export type DateTimeFormatterV2Fn = (date?: DateTypesV2, options?: DateTimeFormatterV2Options) => string;
export type DateTypesV2 = Date | string | number | 'now' | null | DateObject | Dayjs;

export function useDateTimeFormatterV2(userDateFormatOverride?: ThreeLetterDateFormats): DateTimeFormatterV2Fn {
  const { dateTimeFormatConfig } = useDateTimeFormatContext();
  const config = { ...DEFAULT_DATE_TIME_FORMAT, ...dateTimeFormatConfig };
  const targetConfig = { ...config, dateOrder: toDateOrder(userDateFormatOverride) || config.dateOrder };

  return (date, options) => {
    const targetOptions = withDefaultOptions(options, targetConfig);
    if (!date) {
      return targetOptions.noDatePlaceholder;
    }

    const targetDateFormat = buildDateFormatString({
      dateOrder: targetOptions.config.dateOrder,
      display: targetOptions.display,
      format: targetOptions.format,
      separator: targetOptions.config.separator
    });

    const nextDate =
      typeof date === 'string' && date !== 'now' ? date : new DateObject(date === 'now' ? new Date() : date).date;

    return format(
      parseDate(nextDate, !targetOptions.utc, targetOptions.time),
      targetDateFormat,
      typeof targetOptions.timeZone === 'string' ? { timeZone: targetOptions.timeZone } : undefined
    );
  };
}

function toDateOrder(userDateFormatOverride?: ThreeLetterDateFormats): DateFormatModel | undefined {
  if (!userDateFormatOverride) {
    return undefined;
  }
  switch (userDateFormatOverride) {
    case 'dmy':
      return 'DAY_MONTH_YEAR';
    case 'ymd':
      return 'YEAR_MONTH_DAY';
    default:
      return 'MONTH_DAY_YEAR';
  }
}

function withDefaultOptions(
  userOptions: DateTimeFormatterV2Options | undefined,
  config: DateTimeFormatConfigModel
): Utilities.DeeplyDefined<
  Omit<DateTimeFormatterV2Options, 'format' | 'time' | 'use24HrTime'> & { format: Formats; time: boolean }
> {
  const utc = userOptions?.utc ?? false;
  const timeZone = userOptions?.timeZone ?? false;
  const time = userOptions?.time ?? false;
  const numberMonth = userOptions?.numberMonth ?? true;
  const noDatePlaceholder = userOptions?.noDatePlaceholder ?? '-';
  const display =
    userOptions?.display ??
    filterNotEmpty(['YEAR', 'MONTH', 'DAY_OF_MONTH', time ? 'TIME' : false, utc && timeZone ? 'TIMEZONE' : false]);

  return {
    utc,
    timeZone,
    time: !!time,
    noDatePlaceholder,
    display,
    numberMonth,
    config: {
      separator: userOptions?.config?.separator ?? SEPARATOR_DICTIONARY[config.separator],
      dateOrder: userOptions?.config?.dateOrder ?? config.dateOrder
    },
    format: {
      YEAR: YEAR_FORMAT.FULL,
      MONTH: numberMonth ? MONTH_FORMAT.TWO_DIGIT : MONTH_FORMAT.SHORT,
      DAY_OF_MONTH: DAY_OF_MONTH_FORMAT.TWO_DIGIT,
      DAY_OF_WEEK: DAY_OF_WEEK_FORMAT.LONG,
      TIMEZONE: TIMEZONE_FORMAT.GMT,
      TIME: config.use24HrTime
        ? time === 'with_seconds'
          ? TIME_FORMAT.HOURS_MINUTES_SECONDS_24
          : TIME_FORMAT.HOURS_MINUTES_24
        : time === 'with_seconds'
          ? TIME_FORMAT.HOURS_MINUTES_SECONDS_AMPM
          : TIME_FORMAT.HOURS_MINUTES_AMPM,
      ...userOptions?.format
    }
  };
}
